import { DoodMarketplaceModel } from '@core/models/marketplace.model';
import { DoodShopModel } from '@core/models/shop.model';
import { DistributionModeKeys, MarketplaceKeys, ShopKeys } from '@config/keys/shop.keys';
import { normalizeDistributionModeToOrderType } from '@config/values/order.values';
import { SettingsParametersState } from '@store/settings/settings.state';

export const SET_DISTRIBUTION_MODE_QUERY = 'setDistributionMode';
export const FORCE_DISTRIBUTION_MODE_QUERY = 'forceDistributionMode';

/**
 * Parses set/force distribution mode keys from a query string (with or without leading `?`).
 */
export function getDistributionModeQueryParams(search: string): { set?: string; force?: string } {
  const query = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(query);
  const set = params.get(SET_DISTRIBUTION_MODE_QUERY) ?? undefined;
  const force = params.get(FORCE_DISTRIBUTION_MODE_QUERY) ?? undefined;
  const result: { set?: string; force?: string } = {};
  if (set !== undefined) {
    result.set = set;
  }
  if (force !== undefined) {
    result.force = force;
  }
  return result;
}

/**
 * Order type for a newly created cart: URL wins, then normalized settings store, else first allowed handle.
 */
export function resolveOrderTypeForShop(
  allowedHandles: string[],
  parameters: SettingsParametersState,
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): string {
  const { set, force } = getDistributionModeQueryParams(search);
  const raw = set || force;
  if (raw) {
    const normalized = normalizeDistributionModeToOrderType(raw);
    if (allowedHandles.includes(normalized)) {
      return normalized;
    }
  }
  let orderType = normalizeDistributionModeToOrderType(parameters.distribution_mode);
  if (!allowedHandles.includes(orderType)) {
    orderType = allowedHandles[0];
  }
  return orderType;
}

/**
 * Target type when a cart already exists: URL first, then explicit settings if allowed; otherwise null (keep cart).
 */
export function resolveTargetOrderTypeForExistingCart(
  allowedHandles: string[],
  parameters: SettingsParametersState,
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): string | null {
  const { set, force } = getDistributionModeQueryParams(search);
  const raw = set || force;
  if (raw) {
    const normalized = normalizeDistributionModeToOrderType(raw);
    if (allowedHandles.includes(normalized)) {
      return normalized;
    }
  }
  if (parameters.is_distribution_mode_defined && parameters.distribution_mode) {
    const fromSettings = normalizeDistributionModeToOrderType(parameters.distribution_mode);
    if (allowedHandles.includes(fromSettings)) {
      return fromSettings;
    }
  }
  return null;
}

export function resolveAuthModeForOrderType(
  marketplace: DoodMarketplaceModel,
  shop: DoodShopModel,
  orderType: string,
): string | undefined {
  const isMultiShop = marketplace[MarketplaceKeys.CartScope] === 'MARKETPLACE';
  const rootEntityDistributionModes = isMultiShop
    ? marketplace[MarketplaceKeys.DistributionModes]
    : shop?.[ShopKeys.DistributionModes];
  return rootEntityDistributionModes?.find(mode => mode.type === orderType)?.[
    DistributionModeKeys.AuthMode
  ];
}
